import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import adminApi from './api'
import { Notification } from './components/Notification'
import { LocationPickerWithModal } from './components/LocationPickerWithModal'

interface EventOption {
  id: string
  name: string | { ru?: string; ky?: string; en?: string }
}


export const PlantingForm: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = !!id

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [events, setEvents] = useState<EventOption[]>([])

  const [form, setForm] = useState({
    nameRu: '',
    nameKy: '',
    nameEn: '',
    locationName: '',
    latitude: '',
    longitude: '',
    treesPlanted: '',
    plantedAt: new Date().toISOString().slice(0, 10),
    eventId: '',
  })

  useEffect(() => {
    adminApi.get('/api/admin/events').then(({ data }) => {
      setEvents(data.events || [])
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (!isEdit) return
    setLoading(true)
    adminApi.get(`/api/admin/plantings/${id}`)
      .then(({ data }) => {
        const p = data.planting
        setForm({
          nameRu: p.name?.ru || '',
          nameKy: p.name?.ky || '',
          nameEn: p.name?.en || '',
          locationName: p.locationName || '',
          latitude: p.latitude?.toString() || '',
          longitude: p.longitude?.toString() || '',
          treesPlanted: (p.treesPlanted ?? 0).toString(),
          plantedAt: p.plantedAt ? new Date(p.plantedAt).toISOString().slice(0, 10) : '',
          eventId: p.eventId?.toString() || '',
        })
      })
      .catch(err => setError(err?.response?.data?.message || 'Ошибка загрузки'))
      .finally(() => setLoading(false))
  }, [id, isEdit])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!form.latitude || !form.longitude) {
      setError('Выберите локацию на карте')
      return
    }
    if (!form.plantedAt) {
      setError('Укажите дату посадки')
      return
    }

    setSaving(true)
    const payload = {
      name: {
        ru: form.nameRu || undefined,
        ky: form.nameKy || undefined,
        en: form.nameEn || undefined,
      },
      locationName: form.locationName || null,
      latitude: parseFloat(form.latitude),
      longitude: parseFloat(form.longitude),
      treesPlanted: parseInt(form.treesPlanted || '0', 10),
      plantedAt: form.plantedAt,
      eventId: form.eventId ? parseInt(form.eventId, 10) : null,
      // regionId будет определён на бэке автоматически по (latitude, longitude)
    }

    try {
      if (isEdit) {
        await adminApi.put(`/api/admin/plantings/${id}`, payload)
        setNotification({ message: 'Посадка обновлена', type: 'success' })
      } else {
        await adminApi.post('/api/admin/plantings', payload)
        setNotification({ message: 'Посадка создана', type: 'success' })
      }
      setTimeout(() => navigate('/admin/plantings'), 600)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Ошибка сохранения')
    } finally {
      setSaving(false)
    }
  }

  const eventLabel = (ev: EventOption): string => {
    if (typeof ev.name === 'string') return ev.name
    return ev.name?.ru || ev.name?.ky || ev.name?.en || `#${ev.id}`
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="loading-spinner w-10 h-10"></div>
      </div>
    )
  }

  return (
    <div>
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="page-header">
        <h1 className="page-title">{isEdit ? 'Редактировать посадку' : 'Новая посадка'}</h1>
        <p className="page-subtitle">Точка посадки деревьев на карте Кыргызстана</p>
      </div>

      {error && <div className="alert alert-error mb-5">{error}</div>}

      <form onSubmit={handleSubmit} className="admin-form max-w-[900px]">
        <div className="form-section">
          <h3 className="form-section-title">Название (необязательно)</h3>
          <div className="form-grid form-grid-3">
            <div className="form-group">
              <label className="form-label">Русский</label>
              <input
                type="text"
                className="form-input"
                value={form.nameRu}
                onChange={e => setForm({ ...form, nameRu: e.target.value })}
                placeholder="Например, Весенняя посадка 2026"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Кыргызский</label>
              <input
                type="text"
                className="form-input"
                value={form.nameKy}
                onChange={e => setForm({ ...form, nameKy: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">English</label>
              <input
                type="text"
                className="form-input"
                value={form.nameEn}
                onChange={e => setForm({ ...form, nameEn: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3 className="form-section-title">Локация *</h3>
          <LocationPickerWithModal
            latitude={form.latitude}
            longitude={form.longitude}
            locationName={form.locationName}
            onLocationChange={(lat, lng, name) => {
              setForm({ ...form, latitude: lat.toString(), longitude: lng.toString(), locationName: name })
            }}
            onLocationNameChange={(name) => setForm({ ...form, locationName: name })}
          />
          <div style={{ marginTop: 8, font: "400 13px 'Golos Text', sans-serif", color: '#5a6b4d' }}>
            Область на публичной SVG-карте определится автоматически по этим координатам.
          </div>
        </div>

        <div className="form-section">
          <h3 className="form-section-title">Детали</h3>
          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label className="form-label">Дата посадки *</label>
              <input
                type="date"
                className="form-input"
                value={form.plantedAt}
                onChange={e => setForm({ ...form, plantedAt: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Посажено деревьев</label>
              <input
                type="number"
                min={0}
                className="form-input"
                value={form.treesPlanted}
                onChange={e => setForm({ ...form, treesPlanted: e.target.value })}
                placeholder="0"
              />
            </div>
          </div>

          <div className="form-group mt-4">
            <label className="form-label">Связанное событие (необязательно)</label>
            <select
              className="form-input"
              value={form.eventId}
              onChange={e => setForm({ ...form, eventId: e.target.value })}
            >
              <option value="">— Без события —</option>
              {events.map(ev => (
                <option key={ev.id} value={ev.id}>{eventLabel(ev)}</option>
              ))}
            </select>
          </div>

        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/admin/plantings')}
            disabled={saving}
          >
            Отмена
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Сохранение…' : isEdit ? 'Сохранить' : 'Создать'}
          </button>
        </div>
      </form>
    </div>
  )
}
